<template>
    <b-container>
        <b-row>
            <b-col class="px-0 settings-container">
                <print-settings-form
                    v-if="formLayout && formLayout.left"
                    :formData="formLayout.left"
                    @update="$_updatedForm($event)"
                    class="settings-content"
                />
                <div class="button-container">
                    <b-button @click="$emit('print')" variant="primary">{{
                        $t('printComponent.print')
                    }}</b-button>
                    <b-button @click="$emit('cancel')">{{
                        $t('printComponent.cancel')
                    }}</b-button>
                </div>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import PrintSettingsForm from './PrintSettingsForm.vue';
    import { normalNameToPrintSettingsCheckboxName } from '../../services/utilities/utilities';
    export default {
        name: 'PrintSettings',
        components: { 'print-settings-form': PrintSettingsForm },
        props: {
            paperSize: { type: String, required: false, default: 'A4 - staand' },
            dpi: { type: Number, required: false, default: 180 },
            paperSettings: { type: Object, required: true },
            orgScale: { type: Number, required: true },
            currentScale: { type: Number, required: true, default: 100 },
            defaultLogoId: { type: Number, default: 0 },
            logoOptions: { type: Array, required: true }
        },
        data() {
            return {
                selectedDpi: this.dpi,
                dpiChoiceEnabled: false,
                formFields: null
            };
        },
        async created() {
            await this.init();
        },
        mounted() {
            this.$_updatedValue();
        },
        methods: {
            async init() {
                const createFieldFormObject = (field) => ({
                    name: field.id,
                    text: this.$t(
                        `printComponent.${field.id
                            .replace('show', '')
                            .toLowerCase()}`
                    ),
                    valid: null,
                    ...field
                });
                const currentDefaults =
                    this.formFields &&
                    Object.entries(this.formFields).reduce((acc, entry) => {
                        acc[entry[0]] = entry[1].value;
                        return acc;
                    }, {});

                const userPreferences = {
                    ...this.getPrintPrefs,
                    ...currentDefaults
                };

                // formfield configuration for the choice between printing the map, results or both
                const printselection = {
                    id: 'printselection',
                    name: 'printselection',
                    text: this.$t('printComponent.printselection'),
                    type: 'radio',
                    required: true,
                    settings: {},
                    value: userPreferences?.['printselection'] || 'map',
                    valid: null,
                    options: [
                        { text: this.$t('printComponent.chart'), value: 'map' },
                        {
                            text: this.$t(
                                'printComponent.printSelectionMapResults'
                            ),
                            value: 'mapresults'
                        },
                        {
                            text: this.$t('printComponent.printSelectionResults'),
                            value: 'results'
                        }
                    ]
                };
                // formfield configuration for the paperchoice
                const size = {
                    id: 'size',
                    name: 'size',
                    text: this.$t('printComponent.paperSizes'),
                    type: 'select',
                    required: true,
                    settings: {},
                    value: userPreferences?.['size'] || this.paperSize,
                    valid: null,
                    options: this.printSizes
                };

                // formfield configuration for the logo
                const logo = {
                    id: 'logo',
                    name: 'logo',
                    text: this.$t('printComponent.logo'),
                    type: 'select',
                    required: false,
                    settings: {},
                    value: userPreferences?.['logo'] || this.defaultLogoId,
                    valid: null,
                    options: this.logoOptions
                };
                // formfield configuration for the fields configured in the dashboard
                const formfieldData = this.paperSettings.settings.form.map(
                    createFieldFormObject
                );

                // use settings bound to the current selected paper format - template
                const currentformatsettings = Object.entries(
                    this.paperSettings.settings.components
                )
                    // select the required components with optional flag
                    .filter(
                        (component) =>
                            component[1].options.required === true &&
                            component[1].options.optional === true
                    )
                    // create values for a checkbox
                    .map((component) => ({
                        id: normalNameToPrintSettingsCheckboxName(component[0]),
                        value: component[1].options.initial,
                        type: 'checkbox',
                        required: true
                    }))
                    // update common values for a checkbox
                    .map(createFieldFormObject)
                    // add sub formfields for title and freetext
                    .map((formObject) => {
                        if (formObject.id === 'showTitle') {
                            formObject.elements = [
                                {
                                    text: '',
                                    name: 'title',
                                    id: 'title',
                                    type: 'text',
                                    maxlength: 300,
                                    value: ''
                                }
                            ];
                        } else if (formObject.id === 'showFreetext') {
                            formObject.elements = [
                                {
                                    text: '',
                                    name: 'freetext',
                                    id: 'freetext',
                                    type: 'textarea',
                                    maxlength: 300,
                                    value: ''
                                }
                            ];
                        }
                        return formObject;
                    })
                    // update user preference values as set in localstorage
                    .map((formObject) => {
                        if (userPreferences?.[formObject.name] !== undefined) {
                            formObject.value = userPreferences[formObject.name];
                        }
                        return formObject;
                    });
                // formfield configuration for the conditional (sub) fields configured in the dashboard
                // text is set to empty string to omit label
                const formfieldSubElementsData = currentformatsettings
                    .filter(({ elements }) => !!elements)
                    .flatMap(({ elements }) => {
                        return elements
                            .map(createFieldFormObject)
                            .map((item) => ({ ...item, text: '' }));
                    });

                const formfields = {};
                [
                    printselection,
                    size,
                    logo,
                    ...currentformatsettings,
                    ...formfieldData,
                    ...formfieldSubElementsData
                ].forEach((field) => {
                    formfields[field.id] = field;
                });
                if (formfields.scale) {
                    formfields.scale.options.unshift({
                        text: `1:${Math.round(this.orgScale)} ${this.$t(
                            'printComponent.originele maat'
                        )}`,
                        value: this.orgScale
                    });
                    formfields.scale.value = this.currentScale;
                }
                // always show disclaimer on print in web
                if (!this.userIsAuthenticated && formfields['showDisclaimer']) {
                    formfields['showDisclaimer'].value = true;
                }
                // to avoid that a small box appears on print in mobile & web
                if (!this.hasVisibleLayers && formfields.showLegend) {
                    formfields.showLegend.value = false;
                }
                this.formFields = formfields;
            },
            $_updatedForm(data) {
                Object.keys(data).forEach((field) => {
                    this.formFields[field].value = data[field];
                });
                this.$_updatedValue();
            },
            $_updatedValue() {
                if (this.formFields) {
                    const exposedValues = {};
                    Object.keys(this.formFields).forEach((field) => {
                        exposedValues[field] = this.formFields[field].value;
                    });
                    this.$emit('settings', exposedValues);
                }
            },
            $_createPrintFormObject(name, type, id) {
                if (!this.formatSettings) {
                    return undefined;
                }
                const settings = this.formatSettings.settings.form[name];
                if (!settings) {
                    return undefined;
                }
                let formConfig = {
                    id: id,
                    name: id,
                    text: this.$t(`printComponent.${settings.id}`),
                    type,
                    required: settings.required,
                    settings: settings,
                    value: null,
                    valid: null
                };
                if (type === 'text' || type === 'textarea') {
                    formConfig = {
                        ...formConfig,
                        placeholder: settings.id,
                        invalidMessage: this.$t('printComponent.invalidInput'),
                        maxlength: settings.maxLength,
                        headerText: settings.id,
                        value: settings.value
                    };
                }
                return formConfig;
            }
        },
        watch: {
            paperSize: {
                handler: 'init',
                immediate: false
            }
        },
        computed: {
            ...mapState(['printSettings', 'printTableResults', 'selections']),
            ...mapGetters([
                'isMobile',
                'getLegendData',
                'getPrintPrefs',
                'getUserInfo',
                'getLogoId',
                'getOrganizationId',
                'getWebTheme'
            ]),

            userIsAuthenticated: function () {
                return (
                    this.$store.getters.userIsAuthenticated ||
                    this.$store.state.requiresAuthentication
                );
            },
            printSizes: function () {
                if (!this.printSettings) {
                    return undefined;
                }
                return this.printSettings.templates.map(({ title }) => title);
            },
            hasVisibleLayers: function () {
                return this.$store.getters.allActiveLoadedThemes
                    ? this.$store.getters.allActiveLoadedThemes.length > 0
                    : this.getLegendData().length > 0;
            },
            formLayout: function () {
                if (!this.formFields) {
                    return undefined;
                }
                const defaultFields = [this.formFields.size];
                if (this.formFields.previewzoom) {
                    defaultFields.push(this.formFields.previewzoom);
                }
                // Mobile (remove this when migrated to mobile_new)
                if (
                    this.printTableResults &&
                    this.printTableResults.length > 0 &&
                    this.formFields.printselection &&
                    defaultFields
                ) {
                    defaultFields.unshift(this.formFields.printselection);
                }
                // Mobile_new
                if (
                    this.selections.length > 0 &&
                    this.selections[0]?.results &&
                    this.formFields.printselection &&
                    defaultFields
                ) {
                    const printableLayers = this.selections[0]?.results.filter(
                        ({ layer }) => layer.extended.print === true
                    );
                    if (printableLayers.length > 0) {
                        defaultFields.unshift(this.formFields.printselection);
                    }
                }
                // create a list of sub-formelements for activated items
                const hiddenSubFields = Object.entries(this.formFields)
                    .map((field) => ({ id: field[0], elements: field[1].elements }))
                    .filter(
                        ({ elements, id }) =>
                            elements && this.formFields[id].value === false
                    )
                    .flatMap(({ elements }) => elements.map(({ id }) => id));
                // dont offer the option for legend if no layers have been loaded
                if (!this.hasVisibleLayers) {
                    hiddenSubFields.push('showLegend');
                }
                // dont offer the disclaimer settings in web
                if (!this.userIsAuthenticated) {
                    hiddenSubFields.push('showDisclaimer');
                    hiddenSubFields.push('disclaimer');
                } else {
                    // Add logo selection for mobile
                    defaultFields.push(this.formFields.logo);
                }
                // an ordered list of formelements to display
                const mapFields = [
                    'scale',
                    'showDate',
                    'showDisclaimer',
                    'disclaimer',
                    'showLegend',
                    'showLogo',
                    'showNorth',
                    'showScale',
                    'showTitle',
                    'title',
                    'showFreetext',
                    'freetext'
                ]
                    // filter fields that are hidden
                    .filter((fieldName) => !hiddenSubFields.includes(fieldName))
                    // filter fields that are not configured in the dashboard
                    .filter((fieldName) => !!this.formFields[fieldName])
                    // attach the formfield configuration
                    .map((fieldName) => this.formFields[fieldName]);
                return {
                    left: [
                        ...defaultFields,
                        ...(this.formFields.printselection.value !== 'results'
                            ? mapFields
                            : [])
                    ]
                };
            }
        }
    };
</script>

<style scoped>
.button-container {
    display: flex;
    justify-content: flex-end;
}

.container,
.row {
    height: 100%;
}

.settings-container {
    display: flex;
    flex-direction: column;
}

.settings-content {
    flex: 1 1 auto;
    height: 0px;
    overflow-y: auto;
}
</style>
